/**
 * Можно ли сейчас играть звук, или выход спит и звук нужно просто пропустить.
 *
 * ЗАЧЕМ. Phaser с pauseOnBlur (значение по умолчанию, в конфиге игры секции
 * audio нет вовсе) на потере фокуса усыпляет AudioContext. У спящего контекста
 * currentTime стоит, а WebAudioSound.play() каждый раз планирует старт как
 * `source.start(context.currentTime + delay)` — то есть ВСЕ звуки, сыгранные
 * за время в фоне, получают одну и ту же метку времени. Игра в фоне не
 * замирает: действия соперников приходят по сети, реплей и витрина меню
 * крутятся цепочкой setTimeout. На возврате контекст просыпается, и весь
 * накопленный залп стартует одновременно: амплитуды складываются линейно
 * (лимитера в цепочке Phaser нет), выходит громче выставленной громкости и
 * с клиппингом — ровно то, на что жалуются игроки.
 *
 * ПОЧЕМУ НЕ ХВАТАЕТ ОДНОЙ ВИДИМОСТИ. Phaser усыпляет контекст по blur ОКНА, а
 * не по visibilitychange: вкладка остается видимой, когда поверх браузера
 * подняли другое приложение или devtools в отдельном окне. Плюс на возврате
 * visibilitychange приходит РАНЬШЕ window.onfocus, на котором Phaser будит
 * контекст, — то есть в окне между ними вкладка уже видима, а выход еще спит.
 * Поэтому состояние читается у самого контекста, и читается В МОМЕНТ вызова.
 *
 * ПОЧЕМУ ЭТО НЕ МОЖЕТ ЗАГЛУШИТЬ ЗВУК НАВСЕГДА:
 * - никаких флагов и подписок: обе проверки считаются заново на каждый звук,
 *   залипать нечему;
 * - у менеджеров без Web Audio (HTML5, NoAudio) контекста нет — для них выход
 *   считается готовым, иначе на таких платформах звук пропал бы целиком;
 * - если вкладка уже видима И окно в фокусе, а контекст еще спит, будим его
 *   сами: Phaser сделает это и сам, но между visibilitychange и window.onfocus
 *   наш вызов ускоряет пробуждение, и следующий же звук идет как обычно.
 *
 * БЕЗ ФОКУСА КОНТЕКСТ НЕ БУДИМ. Вкладка бывает видимой при неактивном окне
 * (поверх подняли другое приложение, devtools отдельным окном, второй
 * монитор) — именно на это и рассчитан pauseOnBlur. Разбудив контекст здесь,
 * мы бы сняли его насовсем: Phaser сам усыпляет только по следующему blur, а
 * будит по focus, — и матч продолжал бы звучать в колонки, пока игрок
 * занимается другим приложением.
 */

/** Вкладка сейчас в фоне */
export const checkIfPageHidden = () => typeof document !== 'undefined'
  && document.visibilityState === 'hidden';

const SLEEPING_CONTEXT_STATES = ['suspended', 'interrupted', 'closed'];

/**
 * @param {Object} [phaserSoundManager] — this.gameScene.sound
 * @returns {boolean} звук сейчас играть некому
 */
export const checkIfSoundOutputAsleep = (phaserSoundManager) => {
  if (checkIfPageHidden()) {
    return true;
  }

  const context = phaserSoundManager?.context;
  // Менеджер без Web Audio: усыплять нечего, звук идет как обычно
  if (!context) {
    return false;
  }
  if (!SLEEPING_CONTEXT_STATES.includes(context.state)) {
    return false;
  }

  // Вкладка видима и окно в фокусе, а контекст спит — будим (на закрытом
  // будить нечего) и пропускаем ровно этот звук
  const isWindowFocused = typeof document === 'undefined' || document.hasFocus?.() !== false;
  try {
    const resumed = context.state === 'closed' || !isWindowFocused ? null : context.resume?.();
    if (resumed && typeof resumed.catch === 'function') {
      resumed.catch(() => {
      });
    }
  } catch (error) {
    // Синхронный InvalidStateError на старых движках — не наша забота
  }

  return true;
};
